// sysbay's reply shape is {detail, message, meta, result, status}. Only `message` is always
// populated, and it is the generic HTTP reason phrase — "Error", "Unprocessable Entity",
// "Conflict" — so on its own it tells the operator nothing. The human-readable text is in
// `detail` (the Err's summary), and when the failure is a wrapped Err the root cause is at
// `result.cause.message`. (login.js documents the same message/detail trap for the /me gate.)
//
// service-ai uses {error}. RTK Query wraps transport failures as {status, data, message}, so
// `data` is the body to read there. Returns a string, or undefined when there is nothing
// better than the caller's own fallback — so call sites keep their `?? '<fallback>'`.
export interface ApiErrorBody {
  detail?: unknown;
  error?: unknown;
  message?: unknown;
  result?: { cause?: { message?: unknown }; message?: unknown } | null;
}

/** An axios error, an RTK Query error, or a response body. */
export type ApiErrorInput =
  | { response?: { data?: ApiErrorBody }; data?: unknown; status?: number; message?: unknown }
  | ApiErrorBody
  | null
  | undefined;

function errorBody(input: ApiErrorInput): ApiErrorBody | null | undefined {
  if (!input || typeof input !== 'object') {
    return undefined;
  }

  const rtkData = 'data' in input ? (input as { data?: unknown }).data : undefined;
  const axiosData =
    'response' in input
      ? (input as { response?: { data?: ApiErrorBody } }).response?.data
      : undefined;
  const body = axiosData ?? rtkData ?? input;

  if (typeof body === 'string') {
    return { message: body };
  }
  return body as ApiErrorBody;
}

export function apiErrorMessage(errOrBody: ApiErrorInput): string | undefined {
  const body = errorBody(errOrBody);

  const parts: string[] = [];

  const push = (value: unknown) => {
    if (typeof value !== 'string') {
      return;
    }

    // sysbay puts the error's class-name summary ("Err: <sentence>", or a bare "Err$Conflict") in `detail`;
    // the class name is machine output and the sentence repeats `result.message`, so strip the prefix.
    const trimmed = value.replace(/^Err(?:\$\w+)*(?::\s*|$)/, '').trim();

    if (trimmed && !parts.includes(trimmed)) {
      parts.push(trimmed);
    }
  };

  push(body?.detail);
  push(body?.result?.cause?.message);
  push(body?.result?.message);
  push(body?.error);

  if (parts.length) {
    return parts.join(' — ');
  }

  // Last resort: the body's own reason phrase. (For an axios error without a response body,
  // `err.message` — "Request failed with status code 500" — is left to the call site.)
  push(body?.message);

  return parts.length ? parts.pop() : undefined;
}

// Same extraction for an already-serialized Err JSON (e.g. a harness step's persisted
// `statusesOfSteps.<step>.result`), which is what DomainCard renders.
export interface ErrJson {
  cause?: { message?: string };
  message?: string;
  summary?: string;
}

export function errJsonMessage(errJson: ErrJson | null | undefined): string | undefined {
  return errJson?.cause?.message ?? errJson?.message ?? errJson?.summary ?? undefined;
}

/** The API's sentence for a failed request, or the caller's own fallback. */
export const errorText = (err: unknown, fallback: string): string =>
  apiErrorMessage(err as ApiErrorInput) ?? fallback;

/** A 404 from a record's own GET (RTK Query hands over `{status, data}`). */
export const isNotFound = (err: unknown): boolean =>
  (err as { status?: number } | null | undefined)?.status === 404;

/** 409 `stale`: the record changed since it was loaded. Reads the RTK Query error (`{status, data}`) and the axios one. */
export const isStale = (err: unknown): boolean => {
  const { status, data, response } = (err ?? {}) as {
    status?: number;
    data?: unknown;
    response?: { status?: number; data?: unknown };
  };
  const body = (response?.data ?? data) as { result?: { code?: string } } | undefined;
  return (response?.status ?? status) === 409 && body?.result?.code === 'stale';
};
